/**
 * "Find a time" grid (Aziz, 2026-09-27): one row per person, 30 minute columns in Dubai
 * time, each cell coloured by what fills it (off, meeting, shoot, outside working
 * hours) so the organiser lands on a time that works. The top row is everyone
 * together: green where all are free for the whole meeting. Tap a column to pick it.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Block { start: string; end: string; kind: 'off' | 'meeting' | 'shoot'; label: string; note?: string | null }
interface Day { day: string; works: boolean; start?: string | null; end?: string | null; ranges?: [string, string][] }
interface Row { user_id: string; name: string; designations: string[]; days: Day[]; blocks: Block[] }

// The grid spans the day the people actually work, not a fixed 08:00 to 20:00: an evening
// opening (Omar, 20:00 to 21:00 on 1 Oct) could not be picked (2026-10-02).
const STEP = 30;

export const KIND_STYLE: Record<string, { cell: string; label: string }> = {
  free: { cell: 'bg-emerald-500/25 hover:bg-emerald-400/40', label: 'Free' },
  hours: { cell: 'bg-slate-800/70 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(148,163,184,0.12)_3px,rgba(148,163,184,0.12)_6px)]', label: 'Outside working hours' },
  off: { cell: 'bg-rose-500/45', label: 'Off' },
  meeting: { cell: 'bg-violet-500/45', label: 'Meeting' },
  shoot: { cell: 'bg-amber-500/50', label: 'Shoot' },
};

/** The instant of a Dubai wall-clock time on a given day (Dubai has no daylight saving). */
export function dubaiAt(day: string, minutesFromMidnight: number): Date {
  const h = String(Math.floor(minutesFromMidnight / 60)).padStart(2, '0');
  const m = String(minutesFromMidnight % 60).padStart(2, '0');
  return new Date(`${day}T${h}:${m}:00+04:00`);
}

function cellState(row: Row, day: string, t0: Date, t1: Date): { kind: string; label: string } {
  const d = row.days.find((x) => x.day === day);
  const hit = row.blocks.find((b) => new Date(b.start) < t1 && t0 < new Date(b.end));
  if (hit) return { kind: hit.kind, label: hit.label + (hit.note ? `: ${hit.note}` : '') };
  // a split day (10-13, 15-19) is inside only within one of its ranges (2026-09-29)
  const inside = !!d && d.works && ((d.ranges && d.ranges.length)
    ? d.ranges.some(([a, b]) => t0 >= new Date(a) && t1 <= new Date(b))
    : (!!d.start && !!d.end && t0 >= new Date(d.start) && t1 <= new Date(d.end)));
  if (!inside) return { kind: 'hours', label: 'Outside working hours' };
  return { kind: 'free', label: 'Free' };
}

export default function AvailabilityGrid({ userIds, day, minutes, selected, onPick }: {
  userIds: string[]; day: string; minutes: number; selected: string | null; onPick: (iso: string) => void;
}) {
  const q = useQuery({
    queryKey: ['availability-grid', userIds.slice().sort().join(','), day],
    queryFn: async () => (await api.get<Row[]>('/availability/grid', { params: { user_ids: userIds.join(','), day } })).data,
    enabled: userIds.length > 0 && !!day,
  });
  const rows = q.data ?? [];
  const { fromMin, toMin, limit } = useMemo(() => {
    let lo = 8 * 60, hi = 20 * 60, latest: { name: string; end: number } | null = null;
    const mins = (iso: string) => { const d = new Date(iso); const h = Number(d.toLocaleString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', hour12: false })) % 24;
      const sameDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }) === day; return sameDay ? h * 60 + d.getUTCMinutes() : 24 * 60; };
    for (const r of rows) {
      const d = r.days.find((x) => x.day === day);
      const rr = (d?.ranges && d.ranges.length) ? d.ranges : (d?.start && d?.end ? [[d.start, d.end] as [string, string]] : []);
      if (!rr.length) continue;
      lo = Math.min(lo, Math.floor(mins(rr[0][0]) / 60) * 60);
      const end = Math.max(...rr.map(([, b]) => mins(b)));
      hi = Math.max(hi, Math.ceil(end / 60) * 60);
      if (!latest || end < latest.end) latest = { name: r.name, end };
    }
    return { fromMin: lo, toMin: Math.min(hi, 24 * 60), limit: rows.length > 1 ? latest : null };
  }, [rows, day]);
  const cols = useMemo(() => Array.from({ length: Math.max(0, (toMin - fromMin) / STEP) }, (_, i) => fromMin + i * STEP), [fromMin, toMin]);
  const span = Math.max(1, Math.round(minutes / STEP));
  const selIdx = selected ? cols.findIndex((c) => dubaiAt(day, c).toISOString() === new Date(selected).toISOString()) : -1;
  // everyone free for the whole meeting from this column?
  const allFree = (i: number) => rows.length > 0 && i + span <= cols.length && rows.every((r) =>
    Array.from({ length: span }, (_, k) => cellState(r, day, dubaiAt(day, cols[i + k]), dubaiAt(day, cols[i + k] + STEP)).kind === 'free').every(Boolean));
  const now = Date.now();

  if (!userIds.length) return <p className="text-xs text-muted">Pick the people first.</p>;
  if (q.isLoading) return <p className="text-xs text-muted">Loading everyone's day...</p>;

  return (
    <div data-testid="availability-grid">
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="border-collapse text-[11px] min-w-max">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-bg-card px-2 py-1 text-left font-medium text-muted w-24 min-w-[6rem] sm:w-36 sm:min-w-[9rem]">Dubai time</th>
              {cols.map((c) => (
                <th key={c} className="px-0 py-1 font-normal text-muted tabular-nums w-7 min-w-[1.75rem]">
                  {c % 60 === 0 ? String(c / 60).padStart(2, '0') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="sticky left-0 z-10 bg-bg-card px-2 py-1 font-medium text-muted-strong">Everyone</td>
              {cols.map((c, i) => {
                const ok = allFree(i) && dubaiAt(day, c).getTime() > now;
                const inSel = selIdx >= 0 && i >= selIdx && i < selIdx + span;
                return (
                  <td key={c} className="p-0.5">
                    <button type="button" disabled={!ok} onClick={() => onPick(dubaiAt(day, c).toISOString())}
                      title={ok ? `Everyone is free at ${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}` : 'Not everyone is free'}
                      aria-label={`${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}${ok ? ', everyone free' : ''}`}
                      data-testid={ok ? 'grid-free' : undefined}
                      className={cn('block h-6 w-6 rounded-sm', ok ? 'bg-emerald-500/60 hover:bg-emerald-400' : 'bg-slate-800/40',
                        inSel && 'ring-2 ring-sky-400')} />
                  </td>
                );
              })}
            </tr>
            {rows.map((r) => (
              <tr key={r.user_id} data-testid="grid-row">
                <td className="sticky left-0 z-10 bg-bg-card px-2 py-1">
                  <div className="text-muted-strong truncate max-w-[5.5rem] sm:max-w-[8.5rem]">{r.name}</div>
                  {r.designations[0] && <div className="text-[10px] text-muted truncate max-w-[5.5rem] sm:max-w-[8.5rem]">{r.designations[0].replace(/_/g, ' ')}</div>}
                </td>
                {cols.map((c, i) => {
                  const s = cellState(r, day, dubaiAt(day, c), dubaiAt(day, c + STEP));
                  const inSel = selIdx >= 0 && i >= selIdx && i < selIdx + span;
                  return (
                    <td key={c} className="p-0.5">
                      <button type="button" onClick={() => onPick(dubaiAt(day, c).toISOString())} title={`${r.name}: ${s.label}`}
                        className={cn('block h-6 w-6 rounded-sm', KIND_STYLE[s.kind].cell, inSel && 'ring-2 ring-sky-400')} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {limit && limit.end < 24 * 60 && (
        <p className="mt-2 text-[11px] text-muted" data-testid="grid-limit">
          The latest time everyone works this day ends at {String(Math.floor(limit.end / 60)).padStart(2, '0')}:{String(limit.end % 60).padStart(2, '0')}, set by {limit.name}'s hours.
        </p>
      )}
      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-muted" aria-label="Legend">
        {(['free', 'meeting', 'shoot', 'off', 'hours'] as const).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={cn('inline-block h-3 w-3 rounded-sm', KIND_STYLE[k].cell)} />{KIND_STYLE[k].label}</span>
        ))}
      </div>
    </div>
  );
}
